/* Public candidature form — replaces the Fillout integration with a native
 * 3-step form. POSTs to /api/applications, then redirects to /apply/thanks
 * with the calendar booking link.
 */
import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Check, ChevronLeft, ChevronRight, Send, Globe } from "lucide-react";
import axios from "axios";
import CountrySelect from "@/components/CountrySelect";
import { useLang } from "@/context/LangContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const LANG_OPTIONS = [
  { code: "fr", label: "Français" }, { code: "en", label: "English" }, { code: "es", label: "Español" },
  { code: "it", label: "Italiano" }, { code: "pt", label: "Português" }, { code: "de", label: "Deutsch" },
];

const INNOVATION_KEYS = [
  "apply_innovation_ai", "apply_innovation_robot", "apply_innovation_iot",
  "apply_innovation_blockchain", "apply_innovation_marketplace", "apply_innovation_tourism",
  "apply_innovation_energy", "apply_innovation_oeno", "apply_innovation_logistics", "apply_innovation_other",
];

const INTEREST_KEYS = [
  "apply_interest_member",
  "apply_interest_partner",
  "apply_interest_invest",
  "apply_interest_mentor",
  "apply_interest_event",
  "apply_interest_visibility",
  "apply_interest_export",
];

const STEP_KEYS = [
  { id: 0, labelKey: "apply_step_you", shortKey: "apply_step_you_short" },
  { id: 1, labelKey: "apply_step_company", shortKey: "apply_step_company_short" },
  { id: 2, labelKey: "apply_step_expectations", shortKey: "apply_step_expectations_short" },
];

export default function ApplyPage() {
  const { t, lang, setLang } = useLang();
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [categories, setCategories] = useState([]);
  const [form, setForm] = useState({
    first_name: "", last_name: "", email: "", phone: "", linkedin: "",
    consent_rgpd: true,
    company_name: "", website: "", country: "France", category: "",
    job_title: "", annual_revenue: "",
    innovation_types: [],
    description: "",
    interests: "",
  });

  useEffect(() => {
    document.title = t("comp_applypage_90");
    axios.get(`${API}/public/membership-pricing`).then((r) => setCategories(r.data || [])).catch(() => {});
  }, []);

  const update = (patch) => setForm((f) => ({ ...f, ...patch }));
  const toggleInnovation = (item) => setForm((f) => ({
    ...f, innovation_types: f.innovation_types.includes(item)
      ? f.innovation_types.filter((x) => x !== item) : [...f.innovation_types, item],
  }));

  const canContinue = useMemo(() => {
    if (step === 0) {
      return form.first_name.trim() && form.last_name.trim() && /.+@.+\..+/.test(form.email) && form.consent_rgpd;
    }
    if (step === 1) {
      return form.company_name.trim() && form.country.trim() && form.category.trim() && form.description.trim().length >= 30;
    }
    return form.interests.trim();
  }, [step, form]);

  const next = () => {
    if (!canContinue) {
      toast.error(t("apply_form_required_toast"));
      return;
    }
    setStep((s) => Math.min(STEP_KEYS.length - 1, s + 1));
    window.scrollTo({ top: 0, behavior: "smooth" });
  };
  const prev = () => setStep((s) => Math.max(0, s - 1));

  const submit = async () => {
    if (!canContinue) return;
    setSubmitting(true);
    try {
      const payload = {
        first_name: form.first_name.trim(),
        last_name: form.last_name.trim(),
        contact_name: `${form.first_name.trim()} ${form.last_name.trim()}`.trim(),
        email: form.email.trim().toLowerCase(),
        phone: form.phone.trim(),
        linkedin: form.linkedin.trim(),
        job_title: form.job_title.trim(),
        consent_rgpd: !!form.consent_rgpd,
        company_name: form.company_name.trim(),
        website: form.website.trim(),
        country: form.country.trim(),
        category: form.category,
        description: form.description.trim(),
        innovation_types: form.innovation_types.join(", "),
        interests: form.interests.trim(),
        annual_revenue: form.annual_revenue ? Number(form.annual_revenue) : null,
        lang,
        source: "apply_form",
      };
      await axios.post(`${API}/applications`, payload);
      toast.success(t("apply_toast_success"));
      navigate("/apply/thanks", { state: { firstName: form.first_name } });
    } catch (err) {
      const msg = err.response?.data?.detail || t("apply_toast_error");
      toast.error(typeof msg === "string" ? msg : t("apply_toast_error_short"));
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-wine-50/30 via-white to-amber-50/20 px-4 py-12 sm:px-6 lg:px-8" data-testid="apply-page">
      <div className="mx-auto max-w-3xl">
        {/* Language selector */}
        <div className="mb-4 flex justify-end">
          <label className="inline-flex items-center gap-2 rounded-full border border-neutral-200 bg-white px-3 py-1.5 text-xs shadow-sm">
            <Globe className="h-3.5 w-3.5 text-wine-600" />
            <span className="text-neutral-500 font-semibold">{t("apply_lang_label")}</span>
            <select value={lang} onChange={(e) => setLang(e.target.value)} className="bg-transparent text-neutral-700 font-semibold focus:outline-none" data-testid="apply-lang-select">
              {LANG_OPTIONS.map((o) => <option key={o.code} value={o.code}>{o.label}</option>)}
            </select>
          </label>
        </div>
        {/* Header */}
        <div className="mb-8 text-center">
          <Link to="/" className="text-xs font-semibold uppercase tracking-[0.3em] text-wine-600 hover:text-wine-700">{t("comp_applypage_91")}</Link>
          <h1 className="mt-3 font-serif text-4xl font-bold text-navy sm:text-5xl">{t("comp_applypage_92")}</h1>
          <p className="mt-3 text-base text-neutral-600">{t("comp_applypage_93")}</p>
        </div>

        {/* Stepper */}
        <ol className="mb-10 flex items-center justify-between gap-2" data-testid="apply-stepper">
          {STEP_KEYS.map((s, i) => (
            <li key={s.id} className="flex flex-1 items-center gap-2">
              <div className={`grid h-9 w-9 shrink-0 place-items-center rounded-full text-sm font-bold transition-all ${i < step ? "bg-wine-500 text-white" : i === step ? "bg-wine-500 text-white ring-4 ring-wine-200" : "bg-neutral-200 text-neutral-500"}`} data-testid={`step-indicator-${i}`}>
                {i < step ? <Check className="h-4 w-4" /> : i + 1}
              </div>
              <div className="hidden sm:block">
                <div className={`text-[10px] font-bold uppercase tracking-wider ${i <= step ? "text-wine-700" : "text-neutral-400"}`}>{t("apply_step_label")} {i + 1}</div>
                <div className={`text-sm font-semibold ${i === step ? "text-navy" : "text-neutral-500"}`}>{t(s.labelKey)}</div>
              </div>
              {i < STEP_KEYS.length - 1 && <div className={`h-0.5 flex-1 ${i < step ? "bg-wine-500" : "bg-neutral-200"}`} aria-hidden="true" />}
            </li>
          ))}
        </ol>

        <div className="rounded-3xl border border-wine-100 bg-white p-6 shadow-sm sm:p-10">
          {step === 0 && (
            <div className="space-y-5" data-testid="apply-step-0">
              <h2 className="font-serif text-2xl font-bold text-navy">{t("apply_step0_title")}</h2>
              <p className="text-sm text-neutral-600">{t("comp_applypage_94")}</p>
              <div className="grid gap-4 sm:grid-cols-2">
                <Field label={t("comp_applypage_95")}>
                  <input value={form.first_name} onChange={(e) => update({ first_name: e.target.value })} className="input" data-testid="apply-first-name" required />
                </Field>
                <Field label={t("apply_field_lastname")}>
                  <input value={form.last_name} onChange={(e) => update({ last_name: e.target.value })} className="input" data-testid="apply-last-name" required />
                </Field>
                <Field label={t("apply_field_email")}>
                  <input type="email" value={form.email} onChange={(e) => update({ email: e.target.value })} className="input" data-testid="apply-email" required />
                </Field>
                <Field label={t("comp_applypage_96")}>
                  <input type="tel" value={form.phone} onChange={(e) => update({ phone: e.target.value })} className="input" data-testid="apply-phone" placeholder="+33 6 12 34 56 78" />
                </Field>
                <Field label={t("comp_applypage_97")} className="sm:col-span-2">
                  <input value={form.linkedin} onChange={(e) => update({ linkedin: e.target.value })} className="input" data-testid="apply-linkedin" placeholder="https://linkedin.com/in/…" />
                </Field>
              </div>
              <label className="flex cursor-pointer items-start gap-3 rounded-2xl border border-wine-100 bg-wine-50/40 px-4 py-3 text-sm">
                <input type="checkbox" checked={form.consent_rgpd} onChange={(e) => update({ consent_rgpd: e.target.checked })} className="mt-0.5 h-4 w-4 accent-wine-500" data-testid="apply-consent" />
                <span className="text-neutral-700">{t("comp_applypage_98")}</span>
              </label>
            </div>
          )}

          {step === 1 && (
            <div className="space-y-5" data-testid="apply-step-1">
              <h2 className="font-serif text-2xl font-bold text-navy">{t("comp_applypage_99")}</h2>
              <p className="text-sm text-neutral-600">{t("comp_applypage_100")}</p>
              <div className="grid gap-4 sm:grid-cols-2">
                <Field label={t("comp_applypage_101")}>
                  <input value={form.company_name} onChange={(e) => update({ company_name: e.target.value })} className="input" data-testid="apply-company-name" required />
                </Field>
                <Field label={t("apply_field_website")}>
                  <input value={form.website} onChange={(e) => update({ website: e.target.value })} className="input" data-testid="apply-website" placeholder="https://…" />
                </Field>
                <Field label={t("comp_applypage_102")}>
                  <input value={form.job_title} onChange={(e) => update({ job_title: e.target.value })} className="input" data-testid="apply-job-title" placeholder="CEO, CTO, Responsable innovation…" />
                </Field>
                <Field label={t("apply_field_revenue")}>
                  <input type="number" min="0" step="1000" value={form.annual_revenue} onChange={(e) => update({ annual_revenue: e.target.value })} className="input" data-testid="apply-annual-revenue" placeholder="Ex: 250000" />
                  <p className="mt-1 text-[11px] text-neutral-500">{t("comp_applypage_103")}</p>
                </Field>
                <Field label={t("apply_field_country")} className="sm:col-span-2">
                  <CountrySelect value={form.country} onChange={(name) => update({ country: name })} testIdPrefix="apply-country" />
                </Field>
                <Field label={t("comp_applypage_104")} className="sm:col-span-2">
                  <select value={form.category} onChange={(e) => update({ category: e.target.value })} className="input" data-testid="apply-category" required>
                    <option value="">{t("apply_field_choose")}</option>
                    {categories.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                  </select>
                </Field>
              </div>
              <Field label={t("comp_applypage_105")}>
                <div className="flex flex-wrap gap-2">
                  {INNOVATION_KEYS.map((k) => {
                    const label = t(k);
                    return (
                      <button key={k} type="button" onClick={() => toggleInnovation(label)} className={`rounded-full border px-3 py-1.5 text-xs font-semibold transition-all ${form.innovation_types.includes(label) ? "border-wine-500 bg-wine-500 text-white shadow-sm" : "border-neutral-200 bg-white text-neutral-700 hover:border-wine-300 hover:bg-wine-50"}`} data-testid={`apply-innov-${k}`}>
                        {label}
                      </button>
                    );
                  })}
                </div>
              </Field>
              <Field label={t("comp_applypage_106")}>
                <textarea value={form.description} onChange={(e) => update({ description: e.target.value })} className="input h-32 resize-y" data-testid="apply-description" placeholder={t("comp_applypage_107")} minLength={30} required />
                <p className="mt-1 text-[11px] text-neutral-500">{t("apply_desc_min_chars")} ({form.description.length}/30)</p>
              </Field>
            </div>
          )}

          {step === 2 && (
            <div className="space-y-5" data-testid="apply-step-2">
              <h2 className="font-serif text-2xl font-bold text-navy">{t("comp_applypage_108")}</h2>
              <p className="text-sm text-neutral-600">{t("comp_applypage_109")}</p>
              <Field label={t("comp_applypage_110")}>
                <div className="space-y-2">
                  {INTEREST_KEYS.map((k) => {
                    const label = t(k);
                    return (
                      <label key={k} className={`flex cursor-pointer items-center gap-3 rounded-2xl border px-4 py-3 text-sm transition-all ${form.interests === label ? "border-wine-500 bg-wine-50 shadow-sm" : "border-neutral-200 bg-white hover:border-wine-300"}`}>
                        <input type="radio" name="interest" checked={form.interests === label} onChange={() => update({ interests: label })} className="h-4 w-4 accent-wine-500" data-testid={`apply-interest-${k}`} />
                        <span className="font-medium text-neutral-800">{label}</span>
                      </label>
                    );
                  })}
                </div>
              </Field>
              <div className="rounded-2xl bg-amber-50/60 px-5 py-4 text-sm text-amber-900">
                <strong>{t("comp_applypage_111")}</strong>
                <ul className="mt-1 list-disc pl-5 text-amber-800">
                  <li>{t("comp_applypage_112")}</li>
                  <li>{t("comp_applypage_113")}</li>
                  <li>{t("comp_applypage_114")}</li>
                </ul>
              </div>
            </div>
          )}

          {/* Footer nav */}
          <div className="mt-8 flex items-center justify-between border-t border-neutral-100 pt-6">
            {step > 0 ? (
              <button onClick={prev} disabled={submitting} className="inline-flex items-center gap-1.5 rounded-full border border-neutral-200 bg-white px-5 py-2.5 text-sm font-semibold text-neutral-700 hover:bg-neutral-50 disabled:opacity-50" data-testid="apply-prev">
                <ChevronLeft className="h-4 w-4" /> {t("apply_btn_previous")}
              </button>
            ) : <div />}
            {step < STEP_KEYS.length - 1 ? (
              <button onClick={next} className="inline-flex items-center gap-1.5 rounded-full bg-wine-500 px-6 py-2.5 text-sm font-bold text-white shadow-sm hover:bg-wine-600 disabled:opacity-50" data-testid="apply-next">
                {t("apply_btn_continue")} <ChevronRight className="h-4 w-4" />
              </button>
            ) : (
              <button onClick={submit} disabled={submitting || !canContinue} className="inline-flex items-center gap-1.5 rounded-full bg-wine-500 px-6 py-2.5 text-sm font-bold text-white shadow-sm hover:bg-wine-600 disabled:opacity-50" data-testid="apply-submit">
                <Send className="h-4 w-4" /> {submitting ? t("apply_btn_submitting") : t("apply_btn_submit")}
              </button>
            )}
          </div>
        </div>

        <p className="mt-6 text-center text-[11px] text-neutral-500">
          {t("apply_form_privacy_before")} <Link to="/privacy" className="underline hover:text-wine-700">{t("comp_applypage_115")}</Link>{t("apply_form_privacy_after")}
        </p>
      </div>

      <style>{`
        .input { width: 100%; border-radius: 0.75rem; border: 1px solid rgb(229 231 235); padding: 0.625rem 0.875rem; font-size: 0.9375rem; outline: none; transition: all 0.15s; }
        .input:focus { border-color: rgb(190 41 71); box-shadow: 0 0 0 3px rgb(254 226 226); }
      `}</style>
    </div>
  );
}

function Field({ label, children, className = "" }) {
  return (
    <label className={`block ${className}`}>
      <span className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-neutral-600">{label}</span>
      {children}
    </label>
  );
}
